import { useWorkspaceMediaQuery, MediaQueryKind } from "client/Hooks";
import {
	ReactElement,
	Ref,
	forwardRef,
	PropsWithChildren,
	Context,
	createContext,
	useContext,
	useEffect,
	ReadonlyRefObject,
	KeyboardEvent
} from "react";
import { Coordinate, Rect } from "client/Misc/commonTypes";
import { DropOverMobile } from "./DropOverMobile";
import { DropOverDesktop } from "./DropOverDesktop";
import { ContainsDropOversContext } from "client/Contexts/ContainsDropOversContext";

export type AbsoluteCoordinate = {
	kind: "absolute";
	coordinate: Coordinate;
};

export type RectangleTargetAlignment = {
	kind: "rect";
	rect: Rect;
	alignment?: DropOverAlignmentValue;
};

export type DropOverContextData = {
	onClose?(): void;
	registerDescendantsRoots?(
		roots: readonly (HTMLElement | nil)[]
	): () => void;
};

export type VerticalAlignmentValue = "t" | "m" | "b";
export type HorizontalAlignmentValue = "l" | "c" | "r";

export type DropOverAlignmentValue = `${VerticalAlignmentValue}${HorizontalAlignmentValue}`;

export const DropOverContext: Context<DropOverContextData> = createContext<DropOverContextData>(
	{
		onClose: undefined,
		registerDescendantsRoots: undefined
	}
);

export type DescendantRootsRegistration = {
	roots: readonly (HTMLElement | nil)[];
};


export type DropOverProps = {
	target: HTMLElement;
	targetAlignment?: DropOverAlignmentValue
		| undefined
		| AbsoluteCoordinate
		| RectangleTargetAlignment;
	contentAlignment?: DropOverAlignmentValue | undefined;
	onClose?(): void;
	bubbleClick?: boolean;
	hasResponsiveDropOver?: boolean;
	outsideClickIgnoreRefs?: readonly ReadonlyRefObject<HTMLElement | null>[];
	bubbleContextMenu?: boolean;
	closeOnMouseDown?: boolean;
	role?: "listbox" | "dialog";
	id?: string;
	ariaMultiselectable?: boolean;
	ariaLabel?: string;
	onKeyUp?: (args: KeyboardEvent) => void;
	shouldCloseWhenWindowLoosesFocus?: boolean;
	shouldStayOpenOnScroll?: boolean;
};

export const DropOver = forwardRef<HTMLDivElement, PropsWithChildren<DropOverProps>>(
	(props: DropOverProps, forwardedRef: Ref<HTMLDivElement>): ReactElement => {
		const {
			hasResponsiveDropOver = true
		} = props;

		const {
			registerDropOver
		} = useContext(ContainsDropOversContext);

		useEffect(
			(): EffectWithDestructorResult => {
				return registerDropOver?.();
			},
			[registerDropOver]
		);

		const isResponsive: boolean = useWorkspaceMediaQuery(MediaQueryKind.Responsive);

		if (isResponsive && hasResponsiveDropOver) {
			return <DropOverMobile {...props} />;
		}

		return <DropOverDesktop {...props} innerRef={forwardedRef} />;
	}
);

DropOver.displayName = "DropOver";